import { OnDestroy, OnInit, Component, ChangeDetectionStrategy } from '@angular/core';

import { CmsService, WindowRef } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { DownloadsComponent, DownloadsItemComponent } from './types';
import { DownloadFileInfo } from '@app/core/types/download-file-info.type';

@Component({
    selector: 'dp-downloads-cms',
    templateUrl: 'downloads-cms.component.html',
    styleUrls: ['./downloads-cms.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DpDownloadsCmsComponent implements OnInit, OnDestroy {
  items$: Observable<(DownloadFileInfo | undefined)[]>;
  cursor$ = new BehaviorSubject<number>(1);
  destroy$: Subject<void> = new Subject<void>();

  titleText = '';
  totalLength = 0;
  maxDownloadsInBlock = 5;

  private readonly MAX_DOWNLOADS_L = 20;
  private readonly MAX_DOWNLOADS_S = 5;

  constructor(
    private readonly cmsComponentData: CmsComponentData<DownloadsComponent>,
    private readonly cmsService: CmsService,
    private readonly windowRef: WindowRef
  ) {
    this.items$ = combineLatest([this.cursor$, this.cmsComponentData.data$]).pipe(
      tap(([, data]) => (this.titleText = data.titleText ?? '')),
      filter(([, data]) => !!data?.dpDownloadsComponentItems?.length),
      switchMap(([, data]) =>
        combineLatest(
          data.dpDownloadsComponentItems
            .split(' ')
            .map((component) => this.cmsService.getComponentData<DownloadsItemComponent>(component))
        )
      ),
      tap((data) => (this.totalLength = data.length)),
      map((data) => data.map((item) => item?.file).filter(Boolean)),
      map((data) => data.slice(0, this.cursor$.value * this.maxDownloadsInBlock))
    );
  }

  get isExpandable(): boolean {
    return this.totalLength > this.cursor$.value * this.maxDownloadsInBlock;
  }

  ngOnInit(): void {
    this.windowRef.resize$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.maxDownloadsInBlock = result?.target?.innerWidth > 500 ? this.MAX_DOWNLOADS_L : this.MAX_DOWNLOADS_S;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(void 0);
    this.destroy$.complete();
  }

  loadMore(): void {
    this.cursor$.next(this.cursor$.value + 1);
  }
}
